import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { clientApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
    Building, Mail, Lock, User, Briefcase, Loader2, ArrowLeft, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientLogin() {
    const navigate = useNavigate();
    const [isLogin, setIsLogin] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Form state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [companyName, setCompanyName] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            if (isLogin) {
                await clientApi.login(email, password);
                toast.success('Login successful');
                navigate('/client/portal');
            } else {
                if (password.length < 8) {
                    toast.error('Password must be at least 8 characters');
                    setIsLoading(false);
                    return;
                }
                await clientApi.register(email, name, companyName, password);
                toast.success('Registration successful! Please login.');
                setIsLogin(true);
            }
        } catch (error) {
            toast.error(error.message || 'Authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-void flex items-center justify-center p-4">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-purple/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-acid/5 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 w-full max-w-md">
                {/* Back to Home */}
                <Link 
                    to="/"
                    className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-mono text-sm">Back to Home</span>
                </Link>

                <Card className="bg-void-paper border-border">
                    <CardHeader className="text-center pb-2">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <Building className="w-8 h-8 text-cyber-purple" />
                            <h1 className="font-display text-2xl font-bold text-white">Client Portal</h1>
                        </div>
                        <CardTitle className="font-display text-lg text-muted-foreground">
                            {isLogin ? 'Sign in to your account' : 'Create your account'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isLogin && (
                                <>
                                    <div>
                                        <label className="text-muted-foreground text-xs font-mono mb-2 block uppercase tracking-wider">
                                            Your Name
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                type="text"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="John Smith"
                                                className="pl-10 bg-black/20 border-border focus:border-cyber-purple"
                                                required={!isLogin}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-muted-foreground text-xs font-mono mb-2 block uppercase tracking-wider">
                                            Company Name
                                        </label>
                                        <div className="relative">
                                            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                type="text"
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                placeholder="Acme Corp"
                                                className="pl-10 bg-black/20 border-border focus:border-cyber-purple"
                                                required={!isLogin}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div>
                                <label className="text-muted-foreground text-xs font-mono mb-2 block uppercase tracking-wider">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@company.com"
                                        className="pl-10 bg-black/20 border-border focus:border-cyber-purple"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-muted-foreground text-xs font-mono mb-2 block uppercase tracking-wider">
                                    Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="pl-10 pr-10 bg-black/20 border-border focus:border-cyber-purple"
                                        required
                                        minLength={isLogin ? undefined : 8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {!isLogin && (
                                    <p className="text-muted-foreground/50 text-xs mt-1">
                                        Minimum 8 characters
                                    </p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-cyber-purple text-white hover:bg-cyber-purple/80 font-mono uppercase tracking-wider"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : null}
                                {isLogin ? 'Sign In' : 'Create Account'}
                            </Button>
                        </form>

                        <div className="mt-6 pt-6 border-t border-border text-center">
                            <p className="text-muted-foreground text-sm">
                                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                            </p>
                            <Button
                                variant="link"
                                onClick={() => setIsLogin(!isLogin)}
                                className="text-cyber-purple hover:text-cyber-purple/80 font-mono"
                            >
                                {isLogin ? 'Create Account' : 'Sign In'}
                            </Button>
                        </div>

                        <div className="mt-4 text-center">
                            <p className="text-muted-foreground/50 text-xs">
                                Looking for jobs? <Link to="/" className="text-acid hover:underline">Go to Candidate Portal</Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
